import { isSessionRequestDate, isSessionRequestPerson } from './session-requests.js';
import { parseInstant, parseOccurrenceTimingWrite, OCCURRENCE_TIMING_WRITE_FIELDS, parseCalendarDate, addCalendarDays } from '@liquid-spirit/date-time/validation';
import {readOccurrenceDisplayTimeZone} from './occurrence-read-contract.js';

export class EventRequestContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} request: ${detail}`);
    this.name = 'EventRequestContractError';
    this.code = 'INVALID_API_REQUEST';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isObjectId = value => typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);
const isText = value => typeof value === 'string' && value.trim().length > 0;
const isDateInput = isSessionRequestDate;
const eventTypes = new Set(['Feast', 'Holy Day', 'Admin', 'Community']);
const writeFields = new Set(['automaticTimeZone', 'deviceTimeZone', 'title', 'description', 'eventType', 'date', 'startTime', 'endTime', 'bookingEndsAt', 'venues', 'venue', 'imageUrl', 'attendees', 'hosts', 'isOpenExternally']);
OCCURRENCE_TIMING_WRITE_FIELDS.forEach(field => writeFields.add(field));

const isHost = isSessionRequestPerson;

export function readEventIdentifier(value) {
  if (!isObjectId(value)) throw new EventRequestContractError('identifier', 'expected an ObjectId.');
  return value;
}

/** Validate date filters before a list request becomes a failed database cast. */
export function readEventListRequest(params) {
  if (!isRecord(params)
    || (params.eventType !== undefined && !isText(params.eventType))
    || ['startDate', 'endDate'].some(field => params[field] !== undefined && !isDateInput(params[field]))
    || (params.startDate !== undefined && params.endDate !== undefined && new Date(params.startDate) > new Date(params.endDate))) {
    throw new EventRequestContractError('event list', 'expected valid date bounds and an event type string.');
  }
  try {
    if (readOccurrenceDisplayTimeZone(params) !== undefined) {
      for (const field of ['startDate', 'endDate']) if (params[field] !== undefined) parseCalendarDate(params[field]);
      if (params.endDate !== undefined) addCalendarDays(params.endDate, 1);
    }
  } catch (error) { throw new EventRequestContractError('event list', error.message); }
  return params;
}

/** The host mutation accepts persisted people or a new guest contact. */
export function readEventHostsRequest(hosts) {
  if (!Array.isArray(hosts) || hosts.some(host => !isHost(host))) {
    throw new EventRequestContractError('event hosts', 'expected an array of identified people or guest contacts.');
  }
  return { hosts };
}

/** Image updates require a usable URL string before transport. */
export function readEventImageRequest(payload) {
  if (!isRecord(payload) || !isText(payload.imageUrl)) {
    throw new EventRequestContractError('event image', 'expected a non-empty imageUrl.');
  }
  return payload;
}

/** Creation and field updates store typed attendee references, not bare IDs. */
export function readEventWriteRequest(payload, operation = 'update') {
  if (!isRecord(payload)) {
    throw new EventRequestContractError('event write', 'expected an object.');
  }
  if (['reminderPlanRevisions', 'schedulerFence'].some(key => Object.hasOwn(payload, key))) throw new EventRequestContractError('event write', 'scheduling metadata is server-owned.');
  if (payload.automaticTimeZone !== undefined && typeof payload.automaticTimeZone !== 'boolean'
    || payload.deviceTimeZone !== undefined && (typeof payload.deviceTimeZone !== 'string' || payload.deviceTimeZone.length > 100)) throw new EventRequestContractError('event write', 'invalid automatic timezone context.');
  try { if (payload.bookingEndsAt != null) parseInstant(payload.bookingEndsAt); }
  catch { throw new EventRequestContractError(operation, 'invalid booking finish timestamp.'); }
  try { parseOccurrenceTimingWrite(payload); }
  catch (error) { throw new EventRequestContractError('event timing', error.message); }
  const hasCanonicalStart = payload.timeZone !== undefined && (payload.localStart !== undefined || payload.startsAt !== undefined || payload.startTime !== undefined);
  if ((operation === 'update' && !Object.keys(payload).some(field => writeFields.has(field)))
    || (operation === 'create' && (!isText(payload.title) || !eventTypes.has(payload.eventType)
      || !isObjectId(payload.community) || !payload.calendarTiming && !hasCanonicalStart && (!isDateInput(payload.date) || !isDateInput(payload.startTime))))
    || (operation === 'update' && Object.keys(payload).some(field => !writeFields.has(field)))
    || (payload.createdBy !== undefined && !isObjectId(payload.createdBy))
    || (payload.community !== undefined && !isObjectId(payload.community))
    || ['description', 'venue', 'imageUrl'].some(field => payload[field] !== undefined && !isText(payload[field]))
    || (payload.isOpenExternally !== undefined && typeof payload.isOpenExternally !== 'boolean')
    || (operation === 'create' && payload.isOpenExternally !== undefined && payload.eventType !== 'Community')
    || (payload.startTime !== undefined && payload.endTime != null && new Date(payload.endTime) <= new Date(payload.startTime))
    || (payload.title !== undefined && !isText(payload.title))
    || (payload.eventType !== undefined && !eventTypes.has(payload.eventType))
    || (payload.date !== undefined && !isDateInput(payload.date))
    || (payload.startTime !== undefined && !(payload.calendarTiming && payload.startTime === null) && !isDateInput(payload.startTime))
    || (payload.endTime !== undefined && payload.endTime !== null && !isDateInput(payload.endTime))
    || (payload.venues !== undefined && (!Array.isArray(payload.venues) || payload.venues.some(id => !isObjectId(id))))
    || (payload.hosts !== undefined && (!Array.isArray(payload.hosts) || payload.hosts.some(host => !isHost(host))))) {
    throw new EventRequestContractError('event write', 'expected valid event fields for the operation.');
  }
  if (payload.attendees !== undefined && (!Array.isArray(payload.attendees)
    || payload.attendees.some(attendee => !isRecord(attendee)
      || !['User', 'Member', 'Guest'].includes(attendee.type)
      || !isObjectId(attendee.refId)))) {
    throw new EventRequestContractError('event write', 'expected attendees[] with typed 24-character references.');
  }
  return payload;
}
